import { useState } from 'react';
import PropTypes from 'prop-types';
// material
import {
  Card,
  CardHeader,
  CardContent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import { Tooltip, IconButton } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VmDetailModal from './VmDetailModal';
import SetRackPowerButton from './SetRackPowerButton';
import { formatCPUToCores, formatMemoryToMB } from '../../../utils/formatNumber';
import { GATEWAY_POWER_LABEL, VM_POWER_LABEL } from '../../../utils/constants';

VMInfo.propTypes = {
  rackDetails: PropTypes.object,
  jobInProgress: PropTypes.bool,
  title: PropTypes.string,
  testId: PropTypes.string,
  variant: PropTypes.oneOf(['vm', 'gateway'])
};

VMInfo.defaultProps = {
  title: 'VMs',
  testId: 'vm-info',
  variant: 'vm'
};

export default function VMInfo({ rackDetails, jobInProgress, title, testId, variant }) {
  const isGateway = variant === 'gateway';

  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Gateway is a single object; VMs are an array — normalise to an array so the
  // table can map over both with the same code.
  let rows;
  if (isGateway) {
    rows = rackDetails.gateway ? [rackDetails.gateway] : [];
  } else {
    rows = rackDetails.generic_vms;
  }

  const powerConfig = isGateway
    ? {
        endpoint: '/racks/gateway/running',
        label: GATEWAY_POWER_LABEL,
        buildPayload: () => ({})
      }
    : {
        endpoint: '/racks/vm/running',
        label: VM_POWER_LABEL,
        buildPayload: (vm) => ({ vms: [{ name: vm.name }] })
      };

  return (
    <Card data-testid={testId} sx={{ height: '100%' }}>
      <CardHeader title={title} />
      <CardContent>
        <TableContainer component={Paper}>
          <Table aria-label="simple table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {isGateway ? (
                  <>
                    <TableCell align="right">CPU (Cores)</TableCell>
                    <TableCell align="right">Memory (MB)</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell align="right">Arch</TableCell>
                    <TableCell align="right">Persist Data</TableCell>
                    <TableCell align="right">Disks</TableCell>
                    <TableCell align="right">Networks</TableCell>
                  </>
                )}
                <TableCell align="right">Details</TableCell>
                <TableCell align="right">Controls</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {isGateway ? row.vm_name || `${rackDetails.name}-gateway` : row.name}
                  </TableCell>
                  {isGateway ? (
                    <>
                      <TableCell align="right">
                        {formatCPUToCores(row.gateway_resources?.cpu_limit) || 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {formatMemoryToMB(row.gateway_resources?.memory_limit) || 'N/A'}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="right">
                        {row.architecture ? row.architecture : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.persistData ? row.persistData.toString() : 'false'}
                      </TableCell>
                      <TableCell align="right">{row.disks?.length ?? 0}</TableCell>
                      <TableCell align="right">{row.network?.ports?.length ?? 0}</TableCell>
                    </>
                  )}
                  <TableCell align="right">
                    <Tooltip title={`View ${isGateway ? 'gateway' : 'VM'} details`}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedRow(row);
                          setOpen(true);
                        }}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <SetRackPowerButton
                      rackDetails={rackDetails}
                      jobInProgress={jobInProgress}
                      type={row}
                      endpoint={powerConfig.endpoint}
                      label={powerConfig.label}
                      buildPayload={powerConfig.buildPayload}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <VmDetailModal
        open={open}
        onClose={() => setOpen(false)}
        data={selectedRow}
        title={isGateway ? 'Gateway Configuration' : 'VM Configuration'}
        variant={variant}
      />
    </Card>
  );
}
